import type {
  BorderRadiusCore,
  ColorsCore,
  FontFamilyCore,
  FontSizeCore,
  FontWeightCore,
  LineHeightCore,
} from "~/core";

export type ThemeParamsEntry = {
  fontSize?: FontSizeCore;
  lineHeight?: LineHeightCore;
  fontWeight?: FontWeightCore;
  fontFamily?: FontFamilyCore;
  colors?: ColorsCore;
  borderRadius?: BorderRadiusCore;
};
type CreateOverrideThemeEntry = {
  currentThemeEntry: ThemeParamsEntry;
  newThemeEntry?: ThemeParamsEntry;
};

export const createOverrideThemeValues = (
  entry: CreateOverrideThemeEntry,
): ThemeParamsEntry => {
  // COLORS
  const rootThemeColors = entry.currentThemeEntry.colors;
  const pageColors = entry?.newThemeEntry?.colors;
  const differenceColors: ColorsCore = { ...rootThemeColors };
  if (pageColors) {
    for (const key in pageColors) {
      const typedKey = key as keyof typeof pageColors;
      const newValue = pageColors[typedKey];
      const oldValue = rootThemeColors?.[typedKey];

      differenceColors[typedKey] =
        newValue !== null && newValue !== undefined && newValue !== oldValue
          ? newValue
          : oldValue;
    }
  }

  // FONT SIZES
  const rootThemeFontSize = entry.currentThemeEntry.fontSize;
  const pageFontSize = entry?.newThemeEntry?.fontSize;
  const differenceFontSize: FontSizeCore = { ...rootThemeFontSize };
  if (pageFontSize) {
    for (const key in pageFontSize) {
      const typedKey = key as keyof typeof pageFontSize;
      const newValue = pageFontSize[typedKey];
      const oldValue = rootThemeFontSize?.[typedKey];

      differenceFontSize[typedKey] =
        newValue !== null && newValue !== undefined && newValue !== oldValue
          ? newValue
          : oldValue;
    }
  }

  // FONT WEIGHT
  const rootFontWeight = entry.currentThemeEntry.fontWeight;
  const pageFontWeight = entry.newThemeEntry?.fontWeight;
  const differenceFontWeight: FontWeightCore = { ...rootFontWeight };

  if (pageFontWeight) {
    for (const key in pageFontWeight) {
      const typedKey = key as keyof typeof pageFontWeight;
      const newValue = pageFontWeight[typedKey];
      const oldValue = rootFontWeight?.[typedKey];

      differenceFontWeight[typedKey] =
        newValue !== null && newValue !== undefined && newValue !== oldValue
          ? newValue
          : oldValue;
    }
  }

  // Line Height
  const rootLineHeight = entry.currentThemeEntry.lineHeight;
  const pageLineHeight = entry.newThemeEntry?.lineHeight;
  const differenceLineHeight: LineHeightCore = { ...rootLineHeight };
  if (pageLineHeight) {
    for (const key in pageLineHeight) {
      const typedKey = key as keyof typeof pageLineHeight;

      const newValue = pageLineHeight[typedKey];
      const oldValue = rootLineHeight?.[typedKey];

      differenceLineHeight[typedKey] =
        newValue !== null && newValue !== undefined && newValue !== oldValue
          ? newValue
          : oldValue;
    }
  }

  // Font family
  const rootFontFamily = entry.currentThemeEntry.fontFamily;
  const pageFontFamily = entry.newThemeEntry?.fontFamily;

  const differenceFontFamily = {
    main_name: pageFontFamily?.main_name ?? rootFontFamily?.main_name,
    additional_name:
      pageFontFamily?.additional_name ?? rootFontFamily?.additional_name,
    accent_name: pageFontFamily?.accent_name ?? rootFontFamily?.accent_name,
  };

  // Border Radius
  const rootBorderRadius = entry.currentThemeEntry.borderRadius;
  const pageBorderRadius = entry.newThemeEntry?.borderRadius;

  const differenceBorderRadius: BorderRadiusCore = { ...rootBorderRadius };

  if (pageBorderRadius) {
    for (const key in pageBorderRadius) {
      const typedKey = key as keyof typeof pageBorderRadius;
      const newValue = pageBorderRadius[typedKey];
      const oldValue = rootBorderRadius?.[typedKey];

      differenceBorderRadius[typedKey] =
        newValue !== null && newValue !== undefined && newValue !== oldValue
          ? newValue
          : oldValue;
    }
  }

  return {
    fontSize: differenceFontSize,
    lineHeight: differenceLineHeight,
    fontWeight: differenceFontWeight,
    colors: differenceColors,
    fontFamily: differenceFontFamily,
    borderRadius: differenceBorderRadius,
  };
};
